import { OrderSheet } from "@/src/types";
import React, { useMemo, useEffect, useState } from 'react';
import { getSellerProfile } from '../../api/auth';
import {
    getOrderItemAggregationKey,
    getOrderItemDisplayName,
} from '../../utils/orderItemDisplay';

export const CustomerReceiptsView = ({
    data,
    customerAliases,
    orderSheets,
    batchRange,
    sortBy
}: {
    data: any[],
    customerAliases?: Record<string, string>,
    orderSheets?: OrderSheet[],
    batchRange?: { start: number, end: number },
    sortBy?: 'default' | 'name'
}) => {
    const [sellerProfile, setSellerProfile] = useState<any>(null);

    useEffect(() => {
        getSellerProfile().then(setSellerProfile).catch(console.error);
    }, []);

    // We should probably group by Store AND Customer if we want to show "Store Name Delivery" on the card.
    // If a customer ordered from Store A and Store B, do we want 1 receipt or 2?
    // The previous code showed `order.store?.name`.
    // If we want "One receipt per customer regardless of store", we can't easily show "Store A Delivery".
    // However, typically "Receipt" is per shipment.
    // But the user request "주문자별 상품을 묶어서" implies they see multiple lines for same person and want 1.
    // Let's check `reports.service.ts`: `getCustomerReceipts` returns orders.
    // If a customer made 2 orders to the SAME store, they definitely strictly want them merged.
    // If a customer made 2 orders to DIFFERENT stores, merging them might be confusing for delivery (different trucks).
    // BUT the request says "주문자별 상품을 묶어서 하나의 영수증처럼".
    // I will group by (Customer + Store) to be safe for logistics. 
    // Use case: Customer orders 3 items. Then remembers 1 more and orders again. 
    // Seller wants to see 1 receipt with 4 items to pack them together.
    // This implies same delivery. So (Store + Customer) is the right key.

    const aggregatedData = useMemo(() => {
        if (!data) return [];

        // Helper to aggregate based on key
        const aggregate = (list: any[], keyFn: (o: any) => string) => {
            const map: Record<string, any> = {};
            list.filter(order => order.status !== 'CANCELLED').forEach(order => {
                const key = keyFn(order);
                if (!map[key]) {
                    map[key] = {
                        ...order,
                        items: [...(order.items || [])],
                        ids: [order.id] // Keep track of multiple IDs
                    };
                } else {
                    map[key].items = [...map[key].items, ...(order.items || [])];
                    // totalPrice will be recalculated below based on consolidated items
                    map[key].ids.push(order.id);
                }
            });

            // Consolidate items and filter by quantity
            return Object.values(map).map(order => {
                const itemMap: Record<string, any> = {};
                order.items.forEach((item: any) => {
                    if (item.quantity <= 0) return; // Skip cancelled/zero items
                    const itemKey = getOrderItemAggregationKey(item);
                    if (!itemMap[itemKey]) {
                        itemMap[itemKey] = { ...item };
                    } else {
                        itemMap[itemKey].quantity += item.quantity;
                    }
                });

                const consolidatedItems = Object.values(itemMap);
                const totalPrice = consolidatedItems.reduce((sum: number, item: any) => sum + (item.price * item.quantity), 0);

                return {
                    ...order,
                    formattedId: order.ids.join(', '),
                    items: consolidatedItems,
                    totalPrice
                };
            }).filter(order => order.items.length > 0); // Remove receipts with no items
        };

        // Group by orderSheetId/storeId + CustomerName + Phone
        const aggregated = aggregate(data, (o) => {
            const phoneVal = (o.customerPhone || '').replace(/\D/g, '');
            const sheetId = o.orderSheetId || o.storeId || '';
            const name = (o.customerName || '').trim();
            return `${sheetId}-${name}-${phoneVal}`;
        });

        if (sortBy === 'name') {
            aggregated.sort((a, b) => (a.customerName || '').localeCompare(b.customerName || '', 'ko-KR'));
        }

        if (batchRange) {
            return aggregated.slice(batchRange.start, batchRange.end);
        }

        return aggregated;
    }, [data, batchRange, sortBy]);

    return (
        <div className="bixolon-print-area text-black font-sans leading-normal">
            {aggregatedData.map((order, idx) => {
                const orderSheet = orderSheets?.find(s => s.id === order.orderSheetId);
                const deliveryDateStr = orderSheet?.deliveryDate ? new Date(orderSheet.deliveryDate).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' }) : '미정';
                const phoneDigits = (order.customerPhone || '').replace(/\D/g, '');
                const maskedPhone = phoneDigits.length === 11
                    ? phoneDigits.replace(/(\d{3})(\d{4})(\d{4})/, '$1-****-$3')
                    : phoneDigits.length === 10
                        ? phoneDigits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-***-$3')
                        : (order.customerPhone || '');
                const displayName = customerAliases && order.customerPhone && customerAliases[order.customerPhone.replace(/[^0-9]/g, '')]
                    ? `${customerAliases[order.customerPhone.replace(/[^0-9]/g, '')]} (${order.customerName})`
                    : order.customerName;

                return (
                    <div key={idx} className="page-break w-full mb-8 print:mb-0">
                        <div className="text-[9pt] font-black mb-2 pt-2">장봐드림 서비스</div>
                        <div className="border-t-[3px] border-black my-3"></div>
                        <div className="text-[8pt] font-bold pb-1 overflow-hidden" style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>[배송지명] {order.store?.name || '정보 없음'}</div>
                        <div className="text-[8pt] font-medium pb-1">[배송일] {deliveryDateStr}</div>
                        <div className="text-[8pt] font-medium pb-1 mt-1">[고객명] {displayName}</div>
                        <div className="text-[8pt] font-medium pb-2">[연락처] {maskedPhone}</div>
                        <div className="border-t border-black border-dashed my-2"></div>
                        <div className="flex text-[7pt] font-bold pb-1 mb-1">
                            <div className="w-6 shrink-0">연번</div>
                            <div className="flex-1 px-1 text-left">상품명</div>
                            <div className="w-12 shrink-0 text-right">단가</div>
                            <div className="w-6 shrink-0 text-right">수량</div>
                            <div className="w-14 shrink-0 text-right">금액</div>
                        </div>
                        <div className="border-t border-black border-dashed my-2"></div>
                        {(order.items || [])
                            .filter((item: any) => item.quantity > 0)
                            .map((item: any, i: number) => (
                                <div key={i} className="flex text-[8pt] font-medium py-1">
                                    <div className="w-6 shrink-0 text-left">{i + 1}.</div>
                                    <div className="flex-1 px-1 overflow-visible break-words">
                                        {getOrderItemDisplayName(item)}
                                    </div>
                                    <div className="w-12 shrink-0 text-right">{item.price?.toLocaleString()}</div>
                                    <div className="w-6 shrink-0 text-right">{item.quantity}</div>
                                    <div className="w-14 shrink-0 text-right">{(item.price * item.quantity).toLocaleString()}</div>
                                </div>
                            ))}
                        <div className="border-t border-black border-dashed my-2"></div>
                        <div className="flex justify-between items-end my-3">
                            <span className="text-[10px] font-bold pb-1">[합계금액]</span>
                            <span className="text-sm font-black tracking-tighter">{(order.totalPrice || 0).toLocaleString()}원</span>
                        </div>
                        <div className="border-t border-black border-dashed my-2"></div>
                        <div className="text-[7pt] font-medium mt-2 pb-1">[상호명] {sellerProfile?.businessName || '상호명 미등록'}</div>
                        <div className="text-[7pt] font-medium pb-1">[주소] {sellerProfile?.storeAddress || '주소 미등록'}</div>
                        <div className="text-[7pt] font-medium pb-3">[연락처] {sellerProfile?.storeContact || '연락처 미등록'}</div>
                        <div className="border-t-[3px] border-black my-3"></div>
                    </div>
                );
            })}
        </div>
    );
};
